import { ButtonSize, ButtonType, ButtonVariant } from '@xemplo/button';
import { isBoolean } from '@xemplo/common-util';

import { useTableSelection } from '../hooks/use-table-selection';
import { formatNumber } from '../table.helper';
import { TableProps } from '../table.types';

import { ActionBarTestId } from './action-bar';
import * as S from './action-bar.style';
import { ActionBarProps } from './action-bar.types';

export function SelectAllRows<TData>({ table }: ActionBarProps<TData>) {
  const { enableRowSelection, toggleSelectAll } = table.options as TableProps<TData>;
  const {
    rowCount,
    totalSelected,
    hasSelected,
    isAllSelected,
    isAllPageRowsSelected,
    isSomeSelected,
    manualPagination,
    selectAllCounterIsPageSize,
    resetSelection,
  } = useTableSelection(table);
  // When enableRowSelection is boolean and false, do not render the select all button
  // This follows the tanstack guidelines where a boolean value enables/disables the selection
  // for the entire table and therefore, we should not show the select all button
  if (isBoolean(enableRowSelection) && !enableRowSelection) return null;

  const handleClick = (event: React.MouseEvent) => {
    // Edge case handling where select all means only current page
    if (selectAllCounterIsPageSize) {
      // If the current selected count is equal to the row count, it means all eligible rows on the current page are selected
      if (totalSelected === rowCount) {
        resetSelection();
        toggleSelectAll?.(false);
      }
    }

    // Client-side: let TanStack do its own thing
    if (!manualPagination) return table.getToggleAllRowsSelectedHandler()(event);

    // It means it's already all selected so we just toggle and deselect everything
    if (isAllSelected) {
      resetSelection();
      toggleSelectAll?.(false);
      return;
    }

    // We keep the visual row selection which is the current page
    // and flick the switch for the isAllSelected
    if (isAllPageRowsSelected) {
      toggleSelectAll?.(true);
      return;
    }

    // If some rows are selected, we toggle the select all
    // and force all page rows to be selected
    if (isSomeSelected) {
      toggleSelectAll?.(true);
      table.toggleAllPageRowsSelected(true);
    }

    // When the user goes straight to select all
    if (totalSelected === 0) {
      toggleSelectAll?.(true);
      return table.getToggleAllRowsSelectedHandler()(event);
    }
  };

  // Determine if we should show "Deselect all" or "Select all"
  const shouldShowDeselectAll = (() => {
    // Edge case: when selectAllCounterIsPageSize is true, show "Deselect all" when all page rows are selected
    if (selectAllCounterIsPageSize) {
      return totalSelected === rowCount;
    }

    // Server-side pagination: check if all rows across all pages are selected
    if (manualPagination) {
      return isAllSelected;
    }

    // Client-side pagination: check if all rows are selected
    return table.getIsAllRowsSelected();
  })();

  return (
    <S.SelectAllButton
      ariaLabel="Select all rows"
      testId={ActionBarTestId.selector}
      type={ButtonType.Tertiary}
      size={ButtonSize.Small}
      variant={hasSelected ? ButtonVariant.Light : ButtonVariant.Dark}
      onClick={handleClick}
      data-testid={ActionBarTestId.selector}
    >
      {shouldShowDeselectAll
        ? 'Deselect all'
        : `Select all (${formatNumber({ value: rowCount })})`}
    </S.SelectAllButton>
  );
}
